<template>
  <div class="auth-page">
    <div class="auth-bg" style="background-image: url(/images/banner.jpg)"></div>
    <div class="auth-overlay"></div>

    <div class="auth-wrapper">
      <!-- Brand panel -->
      <section class="auth-brand">
        <div class="brand-logo">
          <img src="/logo.jpg" alt="Taekwondo.com.ua">
        </div>
        <div class="brand-badge">
          <span class="badge-dot"></span>
          Закрита система
        </div>
        <h1 class="brand-title">Секретарська<br>система</h1>
        <p class="brand-subtitle">
          Реєстрація учасників, жеребкування та звітність турнірів з тхеквондо в Україні.
        </p>

        <div class="brand-notice">
          <div class="notice-icon"><tkd-icon name="lock"/></div>
          <div>
            <div class="notice-title">Доступ лише за запрошенням</div>
            <div class="notice-text">
              Для отримання доступу зверніться до
              <b>Федерації Тхеквондо України</b>
              <span v-if="contactEmail">
                — <a :href="'mailto:' + contactEmail">{{ contactEmail }}</a>
              </span>
            </div>
          </div>
        </div>
        <div class="brand-flag"><span></span><span></span></div>
      </section>

      <!-- Login card -->
      <section class="auth-card">
        <div class="card-header-text">
          <h2>Вхід</h2>
          <p>Кабінет секретаря</p>
        </div>

        <div v-if="accountHistory.length" class="account-history" aria-label="Попередні акаунти">
          <div class="history-head">
            <span>Попередні акаунти</span>
            <button type="button" class="forget-link" @click="clearAccountHistory">Очистити список</button>
          </div>
          <div v-for="account in accountHistory" :key="account" class="account-entry">
            <button type="button" class="account-select" @click="selectAccount(account)">
              <span class="account-avatar">{{ accountInitials(account) }}</span>
              <span class="account-login">{{ account }}</span>
            </button>
            <button
              type="button"
              class="account-remove"
              title="Видалити акаунт зі списку"
              :aria-label="'Видалити ' + account + ' зі списку'"
              @click="removeAccount(account)"
            >×</button>
          </div>
        </div>

        <form action="/login" @submit.prevent="userLoginForm" autocomplete="on" novalidate>
          <div class="field" :class="{ filled: userLogin.email.value }">
            <input
              id="login"
              name="username"
              ref="login"
              v-model.trim="userLogin.email.value"
              type="text"
              autocomplete="username"
              required
            >
            <label for="login">E-mail або логін</label>
            <span class="field-icon"><tkd-icon name="user"/></span>
          </div>

          <div class="field" :class="{ filled: userLogin.password.value }">
            <input
              id="password"
              name="password"
              ref="password"
              v-model="userLogin.password.value"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              required
            >
            <label for="password">Пароль</label>
            <button
              type="button"
              class="field-toggle"
              @click="showPassword = !showPassword"
              :title="showPassword ? 'Сховати пароль' : 'Показати пароль'"
              :aria-label="showPassword ? 'Сховати пароль' : 'Показати пароль'"
            ><tkd-icon :name="showPassword ? 'eye-off' : 'eye'"/></button>
          </div>

          <transition name="fade">
            <div class="form-error" v-if="errorList.length">
              <div v-for="(error, i) in errorList" :key="i">{{ error }}</div>
            </div>
          </transition>

          <button class="submit-btn" type="submit" :disabled="loading || !canSubmit">
            <span v-if="loading" class="spinner"></span>
            <span>{{ loading ? 'Вхід…' : 'Увійти' }}</span>
          </button>
        </form>

        <div class="card-footer-text">
          Немає облікового запису? Доступ надається
          Федерацією Тхеквондо України.
        </div>
      </section>
    </div>

    <footer class="auth-footer">
      © {{ year }} Taekwondo.com.ua ·
      <router-link to="/privacy-policy">Політика конфіденційності</router-link>
    </footer>
  </div>
</template>
<script>
  import 'src/assets/css/tkd-theme.css';

  const LOGIN_HISTORY_KEY = 'loginHistory';
  const LEGACY_SAVED_KEY = 'savedLogin';
  const MAX_LOGIN_HISTORY = 6;

  export default {
    data () {
      return {
        userLogin: {
          email: { value: '', valid: true, error: '' },
          password: { value: '', valid: true, error: '' },
        },
        localError: '',
        showPassword: false,
        accountHistory: [],
        loading: false,
        contactEmail: 'Taekwondo.com.ua@gmail.com',
        year: new Date().getFullYear()
      }
    },
    computed: {
      errorLogin() {
        return this.$store.getters['user/errorLogin'];
      },
      canSubmit() {
        return this.userLogin.email.value.length > 0 && this.userLogin.password.value.length > 0;
      },
      errorList() {
        let list = [];
        let messages = this.errorLogin ? this.errorLogin.messages : null;
        if (messages) {
          Object.keys(messages).forEach(key => {
            let error = messages[key];
            list.push(Array.isArray(error) ? error[0] : error);
          });
        }
        if (this.localError) {
          list.push(this.localError);
        }
        return list;
      }
    },
    watch: {
      'userLogin.email.value': function () { this.localError = ''; },
      'userLogin.password.value': function () { this.localError = ''; }
    },
    created() {
      // уже авторизований — одразу в кабінет
      let jwt = localStorage.getItem('jwt');
      if (jwt != null && jwt.length > 0) {
        location.href = '/user';
        return;
      }
      this.loadLoginHistory();
    },
    mounted() {
      this.$nextTick(() => {
        let target = this.userLogin.email.value ? this.$refs.password : this.$refs.login;
        if (target) target.focus();
      });
    },
    methods: {
      loadLoginHistory() {
        try {
          const stored = JSON.parse(localStorage.getItem(LOGIN_HISTORY_KEY));
          this.accountHistory = Array.isArray(stored)
            ? stored.filter(login => typeof login === 'string' && login.trim()).slice(0, MAX_LOGIN_HISTORY)
            : [];
        } catch (e) {
          this.accountHistory = [];
        }
        // Remove the legacy record that could contain an encoded password.
        localStorage.removeItem(LEGACY_SAVED_KEY);
      },
      rememberLogin() {
        const login = this.userLogin.email.value.trim();
        if (!login) {
          return;
        }

        const normalized = login.toLowerCase();
        const history = this.accountHistory.filter(item => item.toLowerCase() !== normalized);
        history.unshift(login);
        this.accountHistory = history.slice(0, MAX_LOGIN_HISTORY);

        try {
          localStorage.setItem(LOGIN_HISTORY_KEY, JSON.stringify(this.accountHistory));
        } catch (e) {
          // localStorage недоступний — вхід все одно може продовжитися
        }
      },
      selectAccount(login) {
        this.userLogin.email.value = login;
        this.userLogin.password.value = '';
        this.$nextTick(() => this.$refs.password && this.$refs.password.focus());
      },
      removeAccount(login) {
        this.accountHistory = this.accountHistory.filter(item => item !== login);
        try {
          localStorage.setItem(LOGIN_HISTORY_KEY, JSON.stringify(this.accountHistory));
        } catch (e) {
          // localStorage недоступний — список залишиться тільки в поточній сесії
        }
      },
      clearAccountHistory() {
        this.accountHistory = [];
        localStorage.removeItem(LOGIN_HISTORY_KEY);
      },
      accountInitials(login) {
        return login.trim().slice(0, 2).toUpperCase();
      },
      userLoginForm(){
        if (!this.canSubmit || this.loading) {
          return false;
        }
        let params = { email: this.userLogin.email.value, password: this.userLogin.password.value };
        this.loading = true;
        this.localError = '';
        this.$store.dispatch('user/loginUser', params).then(res => {
          // токен записується в store/user.js при успішному вході
          if (res && res.status == 200 && localStorage.getItem('jwt')) {
            this.rememberLogin();
            // нужно делать так редирект чтобы добавился токен в хедере запроса
            location.href = '/user';
            return;
          }
          this.loading = false;
          if (!res) {
            this.localError = 'Помилка сервера. Спробуйте пізніше.';
          } else if (!this.errorList.length) {
            this.localError = 'Невірний логін або пароль.';
          }
        });

        return false;
      }
    }
  }

</script>


<style scoped>
  .auth-page {
    --blue: var(--tkd-blue);
    --blue-dark: var(--tkd-navy);
    --yellow: var(--tkd-yellow);
    --text: var(--tkd-text);
    --muted: var(--tkd-muted);
    position: relative;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px 72px;
    overflow: hidden;
    font-family: var(--tkd-font);
    background: var(--blue-dark);
  }

  .auth-bg {
    position: absolute;
    inset: 0;
    background: center / cover no-repeat;
    transform: scale(1.08);
    filter: blur(3px);
    animation: slowZoom 30s ease-in-out infinite alternate;
  }

  .auth-overlay {
    position: absolute;
    inset: 0;
    background: var(--tkd-gradient-hero);
  }

  .auth-wrapper {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1040px;
    display: grid;
    grid-template-columns: 1.1fr 1fr;
    gap: 48px;
    align-items: center;
    animation: rise 0.6s ease-out both;
  }

  /* Brand */
  .auth-brand { color: #fff; }

  .brand-logo img {
    width: 84px;
    height: 84px;
    object-fit: cover;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4), 0 0 0 3px rgba(255, 255, 255, 0.12);
  }

  .brand-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 28px;
    padding: 6px 14px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--yellow);
    background: rgba(255, 213, 0, 0.1);
    border: 1px solid rgba(255, 213, 0, 0.35);
  }

  .badge-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--yellow);
    box-shadow: 0 0 0 0 rgba(255, 213, 0, 0.7);
    animation: pulse 2s infinite;
  }

  .brand-title {
    margin: 18px 0 14px;
    font-size: 52px;
    line-height: 1.05;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.02em;
  }

  .brand-subtitle {
    max-width: 440px;
    font-size: 17px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.72);
  }

  .brand-notice {
    display: flex;
    gap: 14px;
    max-width: 460px;
    margin-top: 28px;
    padding: 16px 18px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(6px);
  }

  .notice-icon { font-size: 22px; line-height: 1; }
  .notice-title { font-weight: 700; margin-bottom: 4px; }
  .notice-text { font-size: 14px; line-height: 1.5; color: rgba(255, 255, 255, 0.75); }
  .notice-text a { color: var(--yellow); text-decoration: none; word-break: break-all; }
  .notice-text a:hover { text-decoration: underline; }

  .brand-flag {
    display: flex;
    width: 64px;
    height: 6px;
    margin-top: 28px;
    border-radius: 3px;
    overflow: hidden;
  }
  .brand-flag span { flex: 1; background: var(--blue); }
  .brand-flag span + span { background: var(--yellow); }

  /* Card */
  .auth-card {
    background: rgba(255, 255, 255, 0.97);
    border-radius: 22px;
    padding: 40px 36px 30px;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);
  }

  .card-header-text h2 {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    color: var(--text);
  }

  .card-header-text p {
    margin: 4px 0 28px;
    color: var(--muted);
    font-size: 15px;
  }

  .account-history {
    margin: -8px 0 22px;
    padding: 12px;
    border: 1px solid #e6eaf1;
    border-radius: 12px;
    background: #f8f9fc;
  }

  .history-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
    color: var(--muted);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .account-entry {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  .account-entry + .account-entry {
    margin-top: 4px;
  }

  .account-select {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
    padding: 7px 8px;
    border: 0;
    border-radius: 8px;
    color: var(--text);
    text-align: left;
    background: transparent;
    cursor: pointer;
  }

  .account-select:hover,
  .account-select:focus-visible {
    background: #fff;
    outline: none;
  }

  .account-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: #fff;
    background: var(--blue);
    font-size: 11px;
    font-weight: 800;
  }

  .account-login {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
  }

  .account-remove {
    flex: 0 0 30px;
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 8px;
    color: var(--muted);
    background: transparent;
    cursor: pointer;
    font-size: 20px;
    line-height: 1;
  }

  .account-remove:hover,
  .account-remove:focus-visible {
    color: #b42318;
    background: #fef3f2;
    outline: none;
  }

  .field {
    position: relative;
    margin-bottom: 18px;
  }

  .field input {
    width: 100%;
    height: 58px;
    padding: 22px 52px 6px 16px;
    font-size: 16px;
    color: var(--text);
    background: #f4f6fa;
    border: 1.5px solid #e2e6ee;
    border-radius: 12px;
    outline: none;
    transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  }

  .field input:focus {
    background: #fff;
    border-color: var(--blue);
    box-shadow: 0 0 0 4px rgba(0, 87, 183, 0.12);
  }

  .field label {
    position: absolute;
    left: 17px;
    top: 19px;
    margin: 0;
    font-size: 16px;
    color: var(--muted);
    pointer-events: none;
    transition: all 0.18s ease;
  }

  .field input:focus + label,
  .field.filled label,
  .field input:-webkit-autofill + label {
    top: 9px;
    font-size: 12px;
    font-weight: 600;
    color: var(--blue);
  }

  .field-icon,
  .field-toggle {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 18px;
    opacity: 0.55;
  }

  .field-toggle {
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    transition: opacity 0.2s, background 0.2s;
  }

  .field-toggle:hover { opacity: 1; background: rgba(0, 0, 0, 0.05); }
  .field-toggle:focus-visible { outline: 2px solid var(--blue); opacity: 1; }

  .forget-link { color: var(--muted); text-decoration: none; }
  .forget-link {
    padding: 0;
    border: 0;
    color: var(--muted);
    background: transparent;
    cursor: pointer;
    font-size: inherit;
    font-weight: 600;
  }
  .forget-link:hover,
  .forget-link:focus-visible { color: var(--blue); text-decoration: underline; outline: none; }

  .form-error {
    margin-bottom: 16px;
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 14px;
    color: #b42318;
    background: #fef3f2;
    border: 1px solid #fecdca;
  }

  .submit-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 56px;
    border: 0;
    border-radius: 12px;
    font-size: 17px;
    font-weight: 700;
    color: #fff;
    background: var(--tkd-gradient-btn);
    box-shadow: 0 10px 24px rgba(0, 87, 183, 0.35);
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.2s, opacity 0.2s;
  }

  .submit-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 30px rgba(0, 87, 183, 0.45);
  }

  .submit-btn:active:not(:disabled) { transform: translateY(0); }
  .submit-btn:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }

  .spinner {
    width: 18px;
    height: 18px;
    border: 2.5px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  .card-footer-text {
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid #edf0f5;
    font-size: 13px;
    line-height: 1.5;
    text-align: center;
    color: var(--muted);
  }

  .auth-footer {
    position: absolute;
    bottom: 20px;
    left: 0;
    right: 0;
    z-index: 1;
    text-align: center;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
  }

  .auth-footer a { color: rgba(255, 255, 255, 0.7); }

  .fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
  .fade-enter, .fade-leave-to { opacity: 0; }

  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes rise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
  @keyframes slowZoom { from { transform: scale(1.08); } to { transform: scale(1.18); } }
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(255, 213, 0, 0.6); }
    70% { box-shadow: 0 0 0 8px rgba(255, 213, 0, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 213, 0, 0); }
  }

  @media (max-width: 900px) {
    .auth-wrapper { grid-template-columns: 1fr; gap: 28px; max-width: 480px; }
    .auth-brand { text-align: center; }
    .brand-logo img { width: 68px; height: 68px; }
    .brand-badge { margin-top: 18px; }
    .brand-title { font-size: 36px; }
    .brand-title br { display: none; }
    .brand-subtitle { margin: 0 auto; font-size: 15px; }
    .brand-notice { text-align: left; margin: 20px auto 0; }
    .brand-flag { display: none; }
  }

  @media (max-width: 480px) {
    .auth-card { padding: 28px 20px 22px; border-radius: 18px; }
    .brand-title { font-size: 30px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .auth-bg, .badge-dot, .auth-wrapper { animation: none; }
  }
</style>
